import { sendTrackingLog } from '../data/api'
import { getElement } from '../ui/dom'
import { extractNodeData, sanityCheckUrl } from './vast'

let tracked = new Set()
let trackingInterval = null
let globalLogger = null

// Function to set global logger from index.js
export const setGlobalLoggerInVastTracking = (logger) => {
  globalLogger = logger
}

export const handleVastTracking = (adExchangeId = '', adData) => {
  const vastElement = getElement('mediaElement')
  const urlParams = new URLSearchParams(window.location.search)

  if (trackingInterval !== null || !vastElement) {
    return
  }

  console.log('Vast: Started')
  triggerTrackingEvent('start')

  if (adExchangeId) {
    sendTrackingLog({
      eventType: 'started',
      adexchangeId: adExchangeId
    })
  }

  trackingInterval = setInterval(() => {
    if (!vastElement.duration || vastElement.paused || vastElement.ended) {
      return
    }

    const percent = (vastElement.currentTime / vastElement.duration) * 100

    if (percent >= 25 && !tracked.has('firstQuartile')) {
      tracked.add('firstQuartile')
      triggerTrackingEvent('firstQuartile')
      console.log('Vast: FirstQuartile')

      // Comprehensive logging integration
      if (globalLogger) {
        globalLogger.addEvent('vast_first_quartile', {
          progress_percentage: 25,
          current_time: vastElement.currentTime,
          adexchange_id: adExchangeId,
          url: adData?.url,
          type: adData?.source?.type,
          screen_uuid: urlParams.get('screenUuid'),
          timestamp: new Date().toISOString(),
          id: adData?.id
        })
      }

      if (adExchangeId) {
        sendTrackingLog({
          eventType: 'firstQuartile',
          adexchangeId: adExchangeId
        })
      }
    }

    if (percent >= 50 && !tracked.has('midpoint')) {
      tracked.add('midpoint')
      triggerTrackingEvent('midpoint')
      console.log('Vast: Midpoint')

      // Comprehensive logging integration
      if (globalLogger) {
        globalLogger.addEvent('vast_midpoint', {
          progress_percentage: 50,
          current_time: vastElement.currentTime,
          adexchange_id: adExchangeId,
          url: adData?.url,
          type: adData?.source?.type,
          screen_uuid: urlParams.get('screenUuid'),
          timestamp: new Date().toISOString(),
          id: adData?.id
        })
      }

      if (adExchangeId) {
        sendTrackingLog({
          eventType: 'midpoint',
          adexchangeId: adExchangeId
        })
      }
    }

    if (percent >= 75 && !tracked.has('thirdQuartile')) {
      tracked.add('thirdQuartile')
      triggerTrackingEvent('thirdQuartile')
      console.log('Vast: ThirdQuartile')

      // Comprehensive logging integration
      if (globalLogger) {
        globalLogger.addEvent('vast_third_quartile', {
          progress_percentage: 75,
          current_time: vastElement.currentTime,
          adexchange_id: adExchangeId,
          url: adData?.url,
          type: adData?.source?.type,
          screen_uuid: urlParams.get('screenUuid'),
          timestamp: new Date().toISOString(),
          id: adData?.id
        })
      }

      if (adExchangeId) {
        sendTrackingLog({
          eventType: 'thirdQuartile',
          adexchangeId: adExchangeId
        })
      }
    }
  }, 1000)
}

export const clearVastTracking = () => {
  if (trackingInterval) {
    clearInterval(trackingInterval)
    trackingInterval = null
  }
  tracked.clear()
}

export const createVastOptions = () => ({
  tracking: [],
  stopTracking: [],
  impression: []
})

// Global VAST options instance
let globalVastOptions = createVastOptions()

export const getVastOptions = () => globalVastOptions
export const setVastOptions = (options) => {
  globalVastOptions = options
}

export const setTrackingEvent = (linear, inLine, adexchangeId = '') => {
  try {
    const vastOptions = createVastOptions()
    const trackingEvents = linear.getElementsByTagName('TrackingEvents')[0]

    if (trackingEvents) {
      const trackings = trackingEvents.getElementsByTagName('Tracking')
      for (let i = 0; i < trackings.length; i++) {
        const tracking = trackings[i]
        const event = tracking.getAttribute('event')
        const url = sanityCheckUrl(extractNodeData(tracking))

        if (!vastOptions.tracking[event]) {
          vastOptions.tracking[event] = []
        }
        vastOptions.tracking[event].push(url)
      }
    }

    // Process impression events
    const impressions = inLine.getElementsByTagName('Impression')
    for (let i = 0; i < impressions.length; i++) {
      const impressionUrl = sanityCheckUrl(extractNodeData(impressions[i]), true)
      vastOptions.impression.push(impressionUrl)
    }

    setVastOptions(vastOptions)
  } catch (error) {
    console.error('Error processing tracking events:', error)

    // Track error in processing tracking events
  }
}

export const triggerTrackingEvent = (event) => {
  const trackers = globalVastOptions.tracking[event]
  if (Array.isArray(trackers)) {
    if (!globalVastOptions.stopTracking[event]) {
      trackers.forEach((url) => createTrackingPixel(url))
      globalVastOptions.stopTracking[event] = true
    }
  } else if (typeof trackers === 'object' && time !== null) {
    const progressEvent = trackers[time]
    if (progressEvent && !progressEvent.stopTracking) {
      progressEvent.elements.forEach((url) => createTrackingPixel(url))
      progressEvent.stopTracking = true
    }
  }
}

// Enhanced tracking pixel with fallback methods
export const createTrackingPixel = (url) => {
  if (url) {
    // Try image pixel first (most common method)
    const img = new Image()

    img.onload = () => {
      console.log('✅ Tracking success:', url)
    }

    img.onerror = (e) => {
      console.log('❌ Tracking failed:', url, e)

      // Fallback dengan fetch untuk debug
      tryFetchTracking(url, 'image_error')
    }

    img.src = url
  }
}

// Helper function for fetch tracking fallback
const tryFetchTracking = (url, reason = 'fallback') => {
  // Also try to understand what the server actually returns
  fetch(url, {
    mode: 'cors', // Try CORS first to get response details
    cache: 'no-cache',
    method: 'GET'
  })
    .then(async (response) => {
      console.log(`Fetch with CORS success ${url}`)

      // Try to get response content
      let responseText = ''
      let contentType = ''
      try {
        contentType = response.headers.get('content-type') || 'unknown'
        responseText = await response.text()
      } catch (e) {
        responseText = 'Could not read response body'
      }
    })
    .catch(() => {
      // If CORS fails, try no-cors fallback
      console.log('CORS failed, trying no-cors fallback')

      fetch(url, { mode: 'no-cors', cache: 'no-cache' })
        .then((response) => {
          console.log('Fetch no-cors fallback success', response)
        })
        .catch((err) => {
          console.error('All fetch methods failed:', err)
        })
    })
}
